'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import useScrollReveal from '@/hooks/useScrollReveal';

interface Project {
  title: string;
  category: string;
  description: string;
  outcome: string;
  image: string;
  imageAlt: string;
  accentColor: string;
}

export default function SelectedWork() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal<HTMLDivElement>();

  const projects: Project[] = [
  {
    title: 'Texas Tree Transformations',
    category: 'Service Business',
    description: 'Growing tree service company needed comprehensive digital infrastructure to compete in competitive Dallas market and establish professional brand presence.',
    outcome: 'Established as premium tree service provider with automated marketing systems, improved operational efficiency, and consistent client communication infrastructure.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13fd18271-1772170556682.png",
    imageAlt: 'Professional tree service team working on large tree with specialized equipment',
    accentColor: 'brand-green'
  },
  {
    title: 'Prison Entrepreneurship Program (PEP)',
    category: 'Nonprofit Infrastructure',
    description: 'Transformative nonprofit needed modern digital infrastructure to scale volunteer recruitment, manage complex donor relationships, and streamline educational program delivery.',
    outcome: 'Dramatically improved volunteer recruitment efficiency, enhanced donor engagement through automated systems, and established robust infrastructure for program expansion.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5e7d935-1777416627165.png",
    imageAlt: 'Educational program participants engaged in entrepreneurship training session',
    accentColor: 'brand-orange'
  },
  {
    title: 'Entre Capital',
    category: 'Service Business',
    description: 'Financial services firm needed professional brand communications and marketing materials to effectively present loan products and attract investors.',
    outcome: 'Professional brand presence with polished marketing collateral enabling effective investor relations and borrower acquisition.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe785e5c-1784010832247.png",
    imageAlt: 'Professional financial services presentation materials and brand collateral',
    accentColor: 'brand-plum'
  },
  {
    title: 'RPM Diesel Services',
    category: 'Automation & Workflow',
    description: 'Diesel service company drowning in manual service request intake, spending excessive time on paperwork instead of actual service delivery.',
    outcome: 'Massive reduction in administrative overhead, faster response times, improved customer experience, and direct integration with accounting systems.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_129192826-1777416625002.png",
    imageAlt: 'Mobile service request app interface showing automated workflow system',
    accentColor: 'brand-teal'
  }];


  const teamImages = [
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1c03e109a-1766769888313.png",
    alt: "Team collaborating intensely on creative project in industrial workspace"
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_14481122b-1784915379098.png",
    alt: "Founder working late in urban creative studio with determination"
  },
  {
    src: "https://images.unsplash.com/photo-1580203784503-d5d125edfba9",
    alt: "Brainstorming session with sticky notes and raw creative energy"
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_18c2f2e9f-1766999716632.png",
    alt: "Team celebrating project success in authentic documentary style"
  }];


  return (
    <section id="work" className="py-12 sm:py-16 md:py-24 lg:py-32 bg-brand-offwhite">
      {/* Section Header */}
      <div ref={headerRef} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 mb-12 sm:mb-16 md:mb-20">
        <div className={`text-center transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-brand-plum mb-3 sm:mb-4 font-bold leading-tight">
            Selected Work
          </h2>
          <p className="font-body text-sm sm:text-base md:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
            Real challenges. Structural solutions. Measurable change.
          </p>
        </div>
      </div>

      {/* Full-width project showcases */}
      <div className="space-y-10 sm:space-y-12 md:space-y-16 mb-12 sm:mb-16 md:mb-20">
        {projects.map((project, index) => {
          const { ref: projectRef, isVisible: projectVisible } = useScrollReveal<HTMLDivElement>();
          return (
            <div
              key={index}
              ref={projectRef}
              className={`group relative h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden transition-all duration-1000 hover:shadow-2xl ${projectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}>
          
            {/* Full-width background image */}
            <AppImage
                src={project.image}
                alt={project.imageAlt}
                width={1920}
                height={600}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000" />
          
            
            {/* Dark overlay with gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/50 group-hover:from-black/75 group-hover:via-black/60 group-hover:to-black/45 transition-all duration-500"></div>
            
            {/* Content overlay */}
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 w-full">
                <div className="max-w-3xl">
                  <div className={`inline-block px-3 py-1.5 sm:px-4 sm:py-2 bg-${project.accentColor} text-white text-[10px] sm:text-xs font-body font-bold mb-3 sm:mb-4 uppercase tracking-wider shadow-lg transition-all duration-300 group-hover:scale-105`}>
                    {project.category}
                  </div>
                  <h3 className="font-headline text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-3 sm:mb-4 font-bold leading-tight drop-shadow-2xl">
                    {project.title}
                  </h3>
                  <p className="font-body text-sm sm:text-base md:text-lg text-white/95 mb-4 sm:mb-6 leading-relaxed drop-shadow-lg">
                    {project.description}
                  </p>
                  <div className="flex items-start gap-3 sm:gap-4 bg-white/10 backdrop-blur-md p-3 sm:p-4 border-l-4 border-brand-green shadow-xl transition-all duration-300 group-hover:bg-white/15">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-brand-green flex items-center justify-center shadow-lg">
                      <span className="text-white text-base sm:text-lg font-bold">✓</span>
                    </div>
                    <p className="font-body text-xs sm:text-sm text-white font-bold leading-relaxed">
                      {project.outcome}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Accent bar */}
            <div className={`absolute top-0 left-0 w-1 h-full bg-${project.accentColor} group-hover:w-2 transition-all duration-300`}></div>
          </div>);

        })}
      </div>

      {/* Photo Grid Section - Team at Work */}
      <div ref={gridRef} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 mb-12 sm:mb-16 md:mb-20">
        <div className={`text-center mb-8 sm:mb-10 md:mb-12 transition-all duration-1000 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="font-headline text-2xl sm:text-3xl md:text-4xl text-brand-plum mb-2 sm:mb-3 font-bold">
            How We Work
          </h3>
          <p className="font-body text-sm sm:text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed px-4">
            Collaborative. Intentional. Focused on your success.
          </p>
        </div>
        
        {/* Responsive photo grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {teamImages.map((image, index) => {
            const { ref: imgRef, isVisible: imgVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={imgRef}
                className={`group relative h-[300px] sm:h-[350px] md:h-[400px] overflow-hidden shadow-xl transition-all duration-1000 hover:shadow-2xl ${imgVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
            
              <AppImage
                  src={image.src}
                  alt={image.alt}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
            
              <div className="absolute inset-0 bg-brand-plum/15 group-hover:bg-brand-plum/5 transition-colors duration-500"></div>
            </div>);

          })}
        </div>
      </div>

      {/* CTA */}
      <div ref={ctaRef} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 mt-12 sm:mt-16 md:mt-20">
        <div className={`text-center transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Link
            href="/portfolio"
            className="inline-block px-8 sm:px-10 py-3.5 sm:py-4 bg-brand-teal text-white font-cta font-bold text-sm sm:text-base hover:bg-brand-green transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg active:scale-95 active:shadow-md min-h-[44px] flex items-center justify-center">
            
            <span className="flex items-center gap-2 sm:gap-3">
              View All Work
              <span className="text-base sm:text-lg">→</span>
            </span>
          </Link>
        </div>
      </div>
    </section>);

}